import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Bell, BellOff, Trash2, Plus, RefreshCw } from "lucide-react";

const fmt = (n, d = 2) =>
  n == null || isNaN(n) ? "—" : Number(n).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

export default function PriceAlerts({ embedded = false }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  // Form state
  const [symbol, setSymbol] = useState("");
  const [condition, setCondition] = useState("above");
  const [price, setPrice] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/alerts/price");
      setAlerts(data.alerts || []);
    } catch {
      toast.error("Failed to load alerts");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createAlert = async () => {
    const sym = symbol.trim().toUpperCase();
    if (!sym || !price) { toast.error("Symbol and price required"); return; }
    setCreating(true);
    try {
      await api.post("/alerts/price", {
        symbol: sym,
        condition,
        price: Number(price),
        note: note.trim() || undefined,
        notify_telegram: true,
        notify_email: false,
      });
      setSymbol(""); setPrice(""); setNote("");
      await load();
      toast.success(`Alert created: ${sym} ${condition} ₹${fmt(Number(price))}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create alert");
    } finally {
      setCreating(false);
    }
  };

  const deleteAlert = async (id) => {
    try {
      await api.delete(`/alerts/price/${id}`);
      setAlerts((prev) => prev.filter((a) => a.id !== id));
      toast.success("Alert deleted");
    } catch {
      toast.error("Failed to delete alert");
    }
  };

  const resetAlert = async (id) => {
    try {
      await api.post(`/alerts/price/${id}/reset`);
      await load();
      toast.success("Alert re-armed");
    } catch {
      toast.error("Failed to reset alert");
    }
  };

  const active = alerts.filter((a) => a.active && !a.triggered);
  const triggered = alerts.filter((a) => a.triggered);

  const content = (
    <>
      <PageHeader
        overline="Alerts"
        title="Price Alerts"
        description="Get notified via Telegram or email when any symbol crosses a price level. Alerts auto-trigger once and can be re-armed."
      />
      <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">

        {/* Create alert */}
        <div className="panel p-5">
          <div className="overline mb-4">Create alert</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="overline">Symbol</Label>
              <Input
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                placeholder="NIFTY"
                className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data uppercase"
              />
            </div>
            <div>
              <Label className="overline">Condition</Label>
              <Select value={condition} onValueChange={setCondition}>
                <SelectTrigger className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none bg-[var(--bg-surface)] border-[var(--border)]">
                  <SelectItem value="above">Crosses Above ↑</SelectItem>
                  <SelectItem value="below">Falls Below ↓</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline">Price (₹)</Label>
              <Input
                type="number"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="24000"
                className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data"
              />
            </div>
            <div>
              <Label className="overline">Note (optional)</Label>
              <Input
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="e.g. Support level"
                className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)]"
              />
            </div>
          </div>
          <Button
            onClick={createAlert}
            disabled={creating || !symbol || !price}
            className="mt-4 rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider"
          >
            <Plus className="w-4 h-4 mr-1" /> {creating ? "Creating…" : "CREATE ALERT"}
          </Button>
        </div>

        {/* Active alerts */}
        <div className="panel">
          <div className="p-4 border-b border-[var(--border)] flex items-center gap-2">
            <Bell className="w-4 h-4 txt-profit" />
            <span className="overline">Active alerts ({active.length})</span>
          </div>
          {loading && <div className="p-6 txt-muted text-sm text-center">Loading…</div>}
          {!loading && active.length === 0 && (
            <div className="p-6 txt-muted text-sm text-center">No active alerts. Create one above.</div>
          )}
          {active.map((a) => (
            <div key={a.id} className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)] hover:bg-[var(--bg-surface-2)]">
              <div className="flex items-center gap-3">
                <span className="font-section font-bold">{a.symbol}</span>
                <span className={`text-xs font-section px-2 py-0.5 border ${a.condition === "above" ? "border-emerald-500/40 txt-profit" : "border-red-500/40 txt-loss"}`}>
                  {a.condition === "above" ? "↑ ABOVE" : "↓ BELOW"}
                </span>
                <span className="font-mono-data text-sm">₹{fmt(a.price)}</span>
                {a.note && <span className="txt-muted text-xs">{a.note}</span>}
              </div>
              <Button
                size="sm" variant="ghost"
                onClick={() => deleteAlert(a.id)}
                className="rounded-none h-7 w-7 p-0 text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>

        {/* Triggered alerts */}
        {triggered.length > 0 && (
          <div className="panel">
            <div className="p-4 border-b border-[var(--border)] flex items-center gap-2">
              <BellOff className="w-4 h-4 txt-muted" />
              <span className="overline">Triggered ({triggered.length})</span>
            </div>
            {triggered.map((a) => (
              <div key={a.id} className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)] opacity-60">
                <div className="flex items-center gap-3">
                  <span className="font-section font-bold">{a.symbol}</span>
                  <span className="text-xs txt-muted font-mono-data">
                    {a.condition} ₹{fmt(a.price)} → hit ₹{fmt(a.triggered_price)}
                  </span>
                  <span className="text-xs txt-muted">{a.triggered_at?.slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => resetAlert(a.id)}
                    className="rounded-none h-7 px-2 text-xs txt-secondary hover:text-white"
                    title="Re-arm alert"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> RE-ARM
                  </Button>
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => deleteAlert(a.id)}
                    className="rounded-none h-7 w-7 p-0 text-zinc-500 hover:text-red-400"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  return embedded ? content : <AppShell>{content}</AppShell>;
}
